import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import s from "../assets/styles/TicketsCenter.module.css";
import root from "../assets/styles/Root.module.css";

export default function TicketsPage() {
  const { authFetch, user } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");

  const [openCreate, setOpenCreate] = useState(false);

  // NEW: режим показа закрытых (как у Таймвеб)
  const [closedMode, setClosedMode] = useState(false);

  // NEW: выбранный тикет; автоселекта больше нет
  const [activeId, setActiveId] = useState(null);
  const chatRef = useRef(null);
  const scrollChatToBottom = () => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const fmtStatus = (s) => (s === "closed" ? "Закрыт" : "Открыт");

  const load = async () => {
    setLoading(true);
    const res = await authFetch("/api/support/tickets/");
    const data = res.ok ? await res.json() : [];
    setItems(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Разделяем на открытые/закрытые
  const openItemsRaw = useMemo(() => items.filter(t => t.status !== "closed"), [items]);
  const closedItemsRaw = useMemo(() => items.filter(t => t.status === "closed"), [items]);

  const active = useMemo(() => items.find(t => t.id === activeId) || null, [items, activeId]);

  // При открытии тикета и при приходе новых сообщений — автоскролл вниз
  useEffect(() => {
    const t = setTimeout(scrollChatToBottom, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, active?.messages?.length]);

  const onCreate = async (e) => {
    e.preventDefault(); setError("");
    try {
      const form = new FormData();
      form.append("subject", subject);
      form.append("body", body);
      if (file) form.append("attachment", file);
      const res = await authFetch("/api/support/tickets/", { method: "POST", body: form, headers: {} });
      if (!res.ok) {
        const err = await res.json().catch(()=>({detail:"Ошибка"}));
        throw new Error(err.detail || JSON.stringify(err));
      }
      setSubject(""); setBody(""); setFile(null);
      await load();
      setOpenCreate(false);
    } catch (e) { setError(e.message || "Ошибка"); }
  };

  const renderItem = (t) => {
    const isActive = active?.id === t.id;
    return (
      <div
        key={t.id}
        className={`${s.item} ${isActive ? s.itemActive : ""}`}
        onClick={() => {
          setActiveId(t.id);
          setTimeout(scrollChatToBottom, 0);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setActiveId(t.id)}
      >
        <h4 className={s.itTitle}>{t.subject}</h4>
        <div className={s.itMeta}>
          <span>{new Date(t.created_at).toLocaleString("ru-RU")}</span>
          <span className={s.pill}>
            <span className={t.status === "closed" ? s.dotRed : s.dotGreen}></span>
            {fmtStatus(t.status)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className={s.wrap}>
      <div className={s.top}>
        <h2 className={s.title}>Центр поддержки</h2>
        <button className={root.btnPrimary} onClick={()=>setOpenCreate(true)}>Создать тикет</button>
      </div>

      <div className={s.grid}>
        {/* Левая колонка */}
        <div className={`${s.left} ${closedMode ? s.leftClosedMode : ""}`}>
          <div className={s.search}><input placeholder="Поиск" /></div>

          {/* Активные */}
          <div className={`${s.section} ${s.sectionOpen}`}>
            <div className={s.sectionHead}>
              <button
                className={s.sectionHeadBtn}
                onClick={() => setClosedMode(false)}
                aria-pressed={!closedMode}
              >
                <span className={s.sectionTitle}>Активные</span>
                <span className={s.sectionCount}>{openItemsRaw.length}</span>
              </button>
            </div>
            <div className={s.list}>
              {loading ? (
                <div style={{padding:12}}>Загрузка…</div>
              ) : openItemsRaw.length === 0 ? (
                <div className={s.emptyList}>Активных тикетов нет</div>
              ) : (
                openItemsRaw.map(renderItem)
              )}
            </div>
          </div>

          {/* Закрытые (вкладка снизу, по нажатию — во всю высоту) */}
          <div className={`${s.section} ${s.sectionClosed}`}>
            <div className={s.sectionHead}>
              <button
                className={s.sectionHeadBtn}
                onClick={() => setClosedMode(v => !v)}
                aria-pressed={closedMode}
              >
                <span className={s.sectionTitle}>Закрытые тикеты</span>
                <span className={s.sectionCount}>{closedItemsRaw.length}</span>
                <span className={`${s.caret} ${closedMode ? s.caretUp : ""}`} />
              </button>
            </div>
            <div className={`${s.list} ${closedMode ? "" : s.listCollapsed}`}>
              {loading ? (
                <div style={{padding:12}}>Загрузка…</div>
              ) : closedItemsRaw.length === 0 ? (
                <div className={s.emptyList}>Закрытых тикетов нет</div>
              ) : (
                closedItemsRaw.map(renderItem)
              )}
            </div>
          </div>
        </div>

        {/* Правая колонка */}
        {active ? (
          <div className={s.right}>
            <div className={s.head}>
              <h3 className={s.subject}>Тема обращения: {active.subject}</h3>
              {active.status !== 'closed' && (
                <button
                  className={s.closeBtn}
                  onClick={async()=>{
                    await authFetch(`/api/support/tickets/${active.id}/close/`, { method: 'POST' });
                    await load();
                    // После закрытия — сбрасываем выбор
                    if (activeId === active.id) setActiveId(null);
                  }}
                >
                  Закрыть
                </button>
              )}
            </div>

            <div className={s.chat} ref={chatRef}>
              {error && <div className={s.error}>{error}</div>}
              {active?.messages?.map(m => {
                const isMine = !!(m?.author?.id && user?.id && m.author.id === user.id);
                return (
                  <div key={m.id} className={`${s.bubbleRow} ${isMine ? s.mineRow : ''}`}>
                    <div className={`${s.msg} ${isMine ? s.mine : ''}`}>
                      <div className={s.name}>{isMine ? (user?.username || user?.email || 'Вы') : 'Техподдержка'}</div>
                      <div className={s.meta}>{new Date(m.created_at).toLocaleString("ru-RU")}</div>
                      <div>{m.body}</div>
                      {m.attachment && <div><a href={m.attachment} target="_blank" rel="noreferrer">Вложение</a></div>}
                    </div>
                  </div>
                );
              })}
            </div>

            {active.status !== 'closed' && (
              <div className={s.composer}>
                <textarea placeholder="Ваше сообщение" value={body} onChange={e=>setBody(e.target.value)} />
                <div className={s.actions}>
                  <label className={s.file}>
                    <input type="file" accept="application/pdf,image/jpeg,image/png" onChange={e=>setFile(e.target.files?.[0] || null)} />
                    <span className={s.fileBtn}>Файл</span>
                  </label>
                  <button
                    className={root.btnPrimary}
                    onClick={async(e)=>{
                      e.preventDefault();
                      setError("");
                      try {
                        const form = new FormData();
                        form.append('body', body);
                        if (file) form.append('attachment', file);
                        const res = await authFetch(`/api/support/tickets/${active.id}/reply/`, { method: 'POST', body: form, headers: {} });
                        if (!res.ok) throw new Error('Не удалось отправить');
                        setBody(""); setFile(null);
                        await load();
                        setTimeout(scrollChatToBottom, 0);
                      } catch (e) { setError(e.message || 'Ошибка'); }
                    }}
                  >
                    Отправить
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Плейсхолдер «как у Таймвеб» вместо всей правой карточки
          <div className={s.rightEmpty}>
            <div className={s.placeholderBox}>
              <span>Выберите тикет слева</span>
            </div>
          </div>
        )}
      </div>

      {openCreate && (
        <div className={s.backdrop} onClick={()=>setOpenCreate(false)}>
          <div className={s.modal} onClick={e=>e.stopPropagation()}>
            <h3>Создать тикет</h3>
            <div className={s.field}><label>Тема</label><input value={subject} onChange={e=>setSubject(e.target.value)} /></div>
            <div className={s.field}><label>Сообщение</label><textarea rows={5} value={body} onChange={e=>setBody(e.target.value)} /></div>
            <div className={s.field}><label>Файл</label><input type="file" accept="application/pdf,image/jpeg,image/png" onChange={e=>setFile(e.target.files?.[0] || null)} /></div>
            <div className={s.modalActions}>
              <button className={root.btn} onClick={()=>setOpenCreate(false)}>Отмена</button>
              <button className={root.btnPrimary} onClick={onCreate}>Создать</button>
            </div>
            {error && <div style={{ color: "red" }}>{error}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
